/**
 * "Sorteio automático": the weekly fallback that drafts and starts the next
 * round when no admin did it by the chosen moment. Settings card for the
 * ranking's Config tab, plus its edit modal.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Shuffle, X } from 'lucide-react';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { api } from '../../services/api';
import type { RankingActivation, RankingAutoDraft, RankingDetail } from '../../types';
import {
  ScheduleModeOption,
  WEEKDAY_OPTIONS,
  WeeklyScheduleFields,
  deviceTimeZone,
  formatScheduleMoment,
} from './WeeklyScheduleFields';

/** Below this gap after sign-ups open, few players will have confirmed yet. */
const MIN_GAP_AFTER_SIGNUPS_HOURS = 6;

const DEFAULT_WEEKDAY = 0; // Sunday
const DEFAULT_TIME = '22:00';

/** What a payload without the field means: the fallback is off. */
const AUTO_DRAFT_OFF: RankingAutoDraft = {
  enabled: false,
  weekday: null,
  time: null,
  timezone: 'America/Sao_Paulo',
  nextAutoDraftAt: null,
  lastAutoDraftAt: null,
  description: null,
};

function minutesOfWeek(weekday: number, time: string): number {
  const [hour, minute] = time.split(':').map(Number);
  return weekday * 1440 + hour * 60 + minute;
}

function weekdayShort(weekday: number): string {
  return WEEKDAY_OPTIONS.find((o) => o.value === weekday)?.short.toLowerCase() ?? '';
}

/**
 * How long after sign-ups open the auto-draft fires, when sign-ups open on a
 * fixed schedule. Null under round_end activation (sign-ups reopen whenever the
 * round ends, so there is no fixed gap to show).
 */
function gapAfterSignups(
  activation: RankingActivation,
  weekday: number,
  time: string
): { label: string; hours: number } | null {
  if (activation.mode !== 'scheduled' || activation.weekday === null || !activation.time) return null;

  const week = 7 * 1440;
  const gapMinutes =
    (minutesOfWeek(weekday, time) - minutesOfWeek(activation.weekday, activation.time) + week) % week;
  const hours = gapMinutes / 60;

  return {
    label: `Inscrições abrem ${weekdayShort(activation.weekday)} ${activation.time} → sorteio ${weekdayShort(weekday)} ${time} (${hours < 1 ? `${gapMinutes} min` : `${Math.round(hours)}h`} depois)`,
    hours,
  };
}

function GapNote({ gap }: { gap: { label: string; hours: number } }) {
  const tooSoon = gap.hours < MIN_GAP_AFTER_SIGNUPS_HOURS;
  return (
    <p className={`text-xs mt-2 leading-snug flex items-start gap-1.5 ${tooSoon ? 'text-sunny/90' : 'text-white/40'}`}>
      {tooSoon && <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />}
      <span>
        {gap.label}
        {tooSoon && ' — pouca gente vai ter confirmado presença até lá.'}
      </span>
    </p>
  );
}

export function AutoDraftCard({
  ranking,
  onEdit,
  onChange,
}: {
  ranking: RankingDetail;
  onEdit: () => void;
  onChange: () => void;
}) {
  const [isSkipping, setIsSkipping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoDraft = ranking.autoDraft ?? AUTO_DRAFT_OFF;
  const isActive = ranking.status === 'active';
  const enabled = autoDraft.enabled && autoDraft.weekday !== null && autoDraft.time !== null;
  const gap = enabled ? gapAfterSignups(ranking.activation, autoDraft.weekday!, autoDraft.time!) : null;

  const handleSkip = async () => {
    setIsSkipping(true);
    setError(null);
    try {
      const response = await api.rankings.skipAutoDraft(ranking.id);
      if (!response.success) setError(response.error?.message || 'Erro ao pular');
      onChange();
    } catch (err) {
      console.error('Failed to skip auto-draft:', err);
      setError('Erro ao pular');
    } finally {
      setIsSkipping(false);
    }
  };

  return (
    <Card delay={0.18}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white/70 flex items-center gap-2">
          <Shuffle className="w-4 h-4" />
          Sorteio automático
        </h3>
        {isActive && (
          <button onClick={onEdit} className="text-xs text-turquoise hover:text-turquoise/80 transition-colors">
            Editar
          </button>
        )}
      </div>

      {enabled ? (
        <>
          <p className="text-sm text-white/70">
            Sorteia e inicia <span className="text-turquoise font-semibold">{autoDraft.description}</span>
            {' '}se ninguém sortear antes
          </p>
          <p className="text-xs text-white/40 mt-2 leading-snug">
            Se a rodada já estiver sorteada, só inicia o seu rascunho. Nunca encerra uma rodada:
            se a anterior ainda estiver em andamento, não sorteia e avisa os administradores.
          </p>
          {gap && <GapNote gap={gap} />}
          {autoDraft.nextAutoDraftAt && isActive && (
            <div className="flex items-center justify-between gap-3 mt-3">
              <p className="text-xs text-white/40">
                Próximo: {formatScheduleMoment(autoDraft.nextAutoDraftAt, autoDraft.timezone)}
              </p>
              <button
                onClick={handleSkip}
                disabled={isSkipping}
                className="text-xs text-white/60 hover:text-white underline underline-offset-2 disabled:opacity-50 transition-colors"
              >
                {isSkipping ? 'Pulando…' : 'Pular esta semana'}
              </button>
            </div>
          )}
          {error && <p className="text-coral text-xs mt-2">{error}</p>}
        </>
      ) : (
        <>
          <p className="text-sm text-white/70">Desligado</p>
          <p className="text-xs text-white/40 mt-2 leading-snug">
            A próxima rodada só é sorteada quando um administrador tocar em “Sortear nova rodada”.
          </p>
        </>
      )}
    </Card>
  );
}

/**
 * The form lives in a child that only mounts while the modal is open, so its
 * useState initialisers re-seed from the server on every open — no syncing effect.
 */
export function AutoDraftEditModal({
  isOpen,
  onClose,
  ranking,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  ranking: RankingDetail;
  onSaved: () => void;
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
          onClick={onClose}
        >
          <AutoDraftEditForm ranking={ranking} onClose={onClose} onSaved={onSaved} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function AutoDraftEditForm({
  ranking,
  onClose,
  onSaved,
}: {
  ranking: RankingDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const autoDraft = ranking.autoDraft ?? AUTO_DRAFT_OFF;
  const [enabled, setEnabled] = useState(autoDraft.enabled);
  const [weekday, setWeekday] = useState(autoDraft.weekday ?? DEFAULT_WEEKDAY);
  const [time, setTime] = useState(autoDraft.time ?? DEFAULT_TIME);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const gap = enabled && time ? gapAfterSignups(ranking.activation, weekday, time) : null;

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const response = await api.rankings.update(ranking.id, {
        autoDraft: enabled
          ? { enabled, weekday, time, timezone: deviceTimeZone(autoDraft.timezone) }
          : { enabled: false },
      });
      if (response.success) {
        onSaved();
        onClose();
      } else {
        setError(response.error?.message || 'Erro ao salvar');
      }
    } catch (err) {
      console.error('Failed to update auto-draft settings:', err);
      setError('Erro ao salvar');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      onClick={(e) => e.stopPropagation()}
      className="w-full max-w-sm glass-card rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
    >
      <div className="flex justify-between items-start mb-4">
        <h3 className="font-display text-xl font-bold text-white">Sorteio automático</h3>
        <button onClick={onClose} className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      <p className="text-sm text-white/60 mb-4">
        Um plano B para quando você não puder sortear: no horário escolhido, a rodada é
        sorteada e iniciada com os jogadores ativos — a menos que alguém já tenha feito isso.
      </p>

      <div className="space-y-2 mb-4">
        <ScheduleModeOption
          selected={!enabled}
          onSelect={() => setEnabled(false)}
          title="Desligado"
          description="A rodada só é sorteada quando um administrador sortear."
        />
        <ScheduleModeOption
          selected={enabled}
          onSelect={() => setEnabled(true)}
          title="Toda semana, se ninguém sortear"
          description="Sorteia e inicia no horário escolhido. Um rascunho seu é só iniciado."
        />
      </div>

      {enabled && (
        <div className="mb-6 pt-4 border-t border-white/10">
          <WeeklyScheduleFields
            weekday={weekday}
            time={time}
            onWeekdayChange={setWeekday}
            onTimeChange={setTime}
          />

          {gap && <GapNote gap={gap} />}

          <ul className="text-[11px] text-white/40 mt-3 leading-snug space-y-1 list-disc pl-4">
            <li>Os jogadores sorteados recebem o aviso normal de rodada liberada; você recebe um aviso de que foi automático.</li>
            <li>Grupos incompletos começam esperando você escalar os coringas.</li>
            <li>Não roda se a rodada anterior ainda estiver em andamento, se houver partida contestada, se a temporada já tiver todas as rodadas ou se houver menos de 4 ativos — você é avisado.</li>
          </ul>
        </div>
      )}

      {error && <p className="text-coral text-sm mb-4">{error}</p>}

      <Button onClick={handleSave} fullWidth isLoading={isSaving}>
        Salvar
      </Button>
    </motion.div>
  );
}
